/**
 * Builds `data-testid` segments for eligibility factor list rows (`EligibilityFactors`).
 *
 * **Tests / E2E:** Import {@link getEligibilityFactorItemDataTestId}, {@link getEligibilityFactorItemTextDataTestId},
 * or {@link getEligibilityFactorTestIdSuffix} — do not hard-code full `data-testid` strings. Renaming an i18n
 * message id changes the fingerprint.
 *
 * The fingerprint is 16 hex digits from two independent 32-bit hashes (djb2 + FNV-1a) over the full message id.
 */

/** Prefix for `<li>` / row test ids (pair with {@link getEligibilityFactorTestIdSuffix}). */
export const ELIGIBILITY_FACTOR_ITEM_TEST_ID_PREFIX = 'application-eligibility-factor-item-';

/** Prefix for inner text span test ids. */
export const ELIGIBILITY_FACTOR_ITEM_TEXT_TEST_ID_PREFIX = 'application-eligibility-factor-item-text-';

function djb2Hash32(str: string): number {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    }
    return h >>> 0;
}

function fnv1aHash32(str: string): number {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/** Full 16-hex fingerprint (8 hex djb2 + 8 hex FNV-1a); see module doc. */
export function shortHashForTestId(str: string): string {
    return djb2Hash32(str).toString(16).padStart(8, '0') + fnv1aHash32(str).toString(16).padStart(8, '0');
}

function normalizeEligibilityFactorTestIdSlug(factors: string): string {
    return (
        factors
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || 'unknown'
    );
}

/**
 * Suffix used after {@link ELIGIBILITY_FACTOR_ITEM_TEST_ID_PREFIX} / {@link ELIGIBILITY_FACTOR_ITEM_TEXT_TEST_ID_PREFIX}.
 * Import from tests or E2E helpers so assertions stay in sync with the UI (do not hard-code full strings).
 */
export function getEligibilityFactorTestIdSuffix(factors: string, disambiguator?: number): string {
    const testIdSlug = normalizeEligibilityFactorTestIdSlug(factors);
    const idFingerprint = shortHashForTestId(factors);
    return disambiguator != null
        ? `${testIdSlug}-${idFingerprint}--${disambiguator}`
        : `${testIdSlug}-${idFingerprint}`;
}

export function getEligibilityFactorItemDataTestId(factors: string, disambiguator?: number): string {
    return `${ELIGIBILITY_FACTOR_ITEM_TEST_ID_PREFIX}${getEligibilityFactorTestIdSuffix(factors, disambiguator)}`;
}

export function getEligibilityFactorItemTextDataTestId(factors: string, disambiguator?: number): string {
    return `${ELIGIBILITY_FACTOR_ITEM_TEXT_TEST_ID_PREFIX}${getEligibilityFactorTestIdSuffix(factors, disambiguator)}`;
}

/**
 * When the same eligibility factor message id appears more than once, returns 0, 1, … per occurrence
 * so data-testid values can be disambiguated. Unique ids get undefined (no suffix).
 */
export function getEligibilityFactorTestIdDisambiguators(factors: string[]): (number | undefined)[] {
    const counts = new Map<string, number>();
    factors.forEach((f) => {
        counts.set(f, (counts.get(f) ?? 0) + 1);
    });
    const seen = new Map<string, number>();
    return factors.map((f) => {
        if ((counts.get(f) ?? 0) <= 1) {
            return undefined;
        }
        const n = seen.get(f) ?? 0;
        seen.set(f, n + 1);
        return n;
    });
}
